import { useState, useEffect } from 'react'
import { X, Copy, Check, MessageCircle, Users } from 'lucide-react'
import { T } from '../theme'

/**
 * ShareModal — modale "Partager Insightball".
 *
 * Affichée depuis l'écran Benchmark verrouillé pour aider le coach à
 * activer son comparatif plus vite. Deux actions possibles :
 *   1. Copier un message d'invitation préformaté
 *   2. Rejoindre la communauté WhatsApp Insightball
 */

const WHATSAPP_COMMUNITY_URL = 'https://chat.whatsapp.com/KRz1ZyXKlvFLxb90GtaM1N?mode=gi_t'

const SHARE_MESSAGE = `Salut, je teste un outil pour analyser nos matchs (stats, suivi joueurs, progression).
Ça s'appelle Insightball, c'est fait pour le foot amateur, gratuit pour démarrer.

Tu jettes un oeil ? https://insightball.com`

export default function ShareModal({ isOpen, onClose, segmentLabel }) {
  const [copied, setCopied] = useState(false)
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth < 768)

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768)
    window.addEventListener('resize', check)
    return () => window.removeEventListener('resize', check)
  }, [])

  useEffect(() => {
    if (isOpen) setCopied(false)
  }, [isOpen])

  if (!isOpen) return null

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(SHARE_MESSAGE)
      setCopied(true)
      setTimeout(() => setCopied(false), 3000)
    } catch (e) {
      console.error('Erreur copie :', e)
    }
  }

  const handleJoinCommunity = () => {
    window.open(WHATSAPP_COMMUNITY_URL, '_blank', 'noopener,noreferrer')
  }

  return (
    <div
      onClick={(e) => { if (e.target === e.currentTarget) onClose() }}
      style={{
        position: 'fixed', inset: 0, zIndex: 200,
        background: 'rgba(10,9,8,0.75)',
        backdropFilter: 'blur(4px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 16,
      }}>
      <div style={{
        background: T.surface, maxWidth: 520, width: '100%',
        border: `1px solid ${T.rule}`,
        borderTop: `2px solid ${T.gold}`,
        animation: 'modalIn .25s ease',
      }}>
        <style>{`@keyframes modalIn { from { opacity:0; transform:translateY(8px); } to { opacity:1; transform:translateY(0); } }`}</style>

        {/* Header */}
        <div style={{ padding: '20px 24px', borderBottom: `1px solid ${T.rule}`, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16 }}>
          <div>
            <div style={{ fontFamily: T.mono, fontSize: 9, letterSpacing: '.18em', textTransform: 'uppercase', color: T.gold, display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <span style={{ width: 14, height: 1, background: T.gold }} />
              Partager Insightball
            </div>
            <h2 style={{ fontFamily: T.display, fontSize: isMobile ? 22 : 26, textTransform: 'uppercase', lineHeight: 1, color: T.ink, margin: 0, letterSpacing: '.01em' }}>
              Active ton<br /><span style={{ color: T.gold }}>comparatif plus vite.</span>
            </h2>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: `1px solid ${T.rule}`, padding: 6, cursor: 'pointer', flexShrink: 0 }}>
            <X size={14} color={T.muted} />
          </button>
        </div>

        {/* Intro */}
        <div style={{ padding: '20px 24px', background: T.bg, borderBottom: `1px solid ${T.rule}` }}>
          <p style={{ fontFamily: T.mono, fontSize: 11, color: T.ink, lineHeight: 1.6, margin: 0 }}>
            Le comparatif {segmentLabel ? <strong>{segmentLabel}</strong> : 'national'} s'active dès que <strong>5 clubs de ton niveau</strong> utilisent Insightball.
            Tu peux aller plus vite en partageant l'outil avec d'autres staffs.
          </p>
        </div>

        {/* Actions */}
        <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 12 }}>

          {/* Action 1 — Partager message */}
          <div style={{ border: `1px solid ${T.rule}`, padding: '16px 18px' }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 12 }}>
              <div style={{
                width: 36, height: 36, flexShrink: 0,
                background: T.goldBg, border: `1px solid ${T.goldBdr}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <Copy size={16} color={T.gold} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontFamily: T.display, fontSize: 16, textTransform: 'uppercase', color: T.ink, letterSpacing: '.02em', marginBottom: 4 }}>
                  Partager à un coach
                </div>
                <p style={{ fontFamily: T.mono, fontSize: 10, color: T.muted, lineHeight: 1.5, margin: 0 }}>
                  Copie le message et envoie-le par WhatsApp ou SMS.
                </p>
              </div>
            </div>

            {/* Aperçu du message */}
            <div style={{
              background: T.bg, border: `1px solid ${T.rule}`,
              padding: '12px 14px', marginBottom: 12,
              fontFamily: T.mono, fontSize: 11, color: T.ink, lineHeight: 1.6,
              whiteSpace: 'pre-wrap',
            }}>
              {SHARE_MESSAGE}
            </div>

            <button
              onClick={handleCopy}
              style={{
                width: '100%',
                padding: '11px 18px',
                background: copied ? '#22c55e' : T.gold,
                color: copied ? '#fff' : T.dark,
                fontFamily: T.mono, fontSize: 10, letterSpacing: '.14em', textTransform: 'uppercase', fontWeight: 700,
                border: 'none', cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                transition: 'background .2s',
              }}>
              {copied ? <><Check size={14} /> Message copié</> : <><Copy size={14} /> Copier le message</>}
            </button>
          </div>

          {/* Action 2 — WhatsApp Community */}
          <div style={{ border: `1px solid ${T.rule}`, padding: '16px 18px' }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 12 }}>
              <div style={{
                width: 36, height: 36, flexShrink: 0,
                background: 'rgba(37,211,102,0.1)', border: '1px solid rgba(37,211,102,0.3)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <Users size={16} color="#25d366" />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontFamily: T.display, fontSize: 16, textTransform: 'uppercase', color: T.ink, letterSpacing: '.02em', marginBottom: 4 }}>
                  Rejoindre les coachs Insightball
                </div>
                <p style={{ fontFamily: T.mono, fontSize: 10, color: T.muted, lineHeight: 1.5, margin: 0 }}>
                  Discussion entre staffs amateurs. Groupe ouvert.
                </p>
              </div>
            </div>

            <button
              onClick={handleJoinCommunity}
              style={{
                width: '100%',
                padding: '11px 18px',
                background: '#25d366',
                color: '#fff',
                fontFamily: T.mono, fontSize: 10, letterSpacing: '.14em', textTransform: 'uppercase', fontWeight: 700,
                border: 'none', cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              }}>
              <MessageCircle size={14} /> Rejoindre le groupe
            </button>
          </div>
        </div>

        {/* Footer */}
        <div style={{ padding: '14px 24px', borderTop: `1px solid ${T.rule}`, background: T.bg, textAlign: 'center' }}>
          <button onClick={onClose} style={{
            background: 'transparent', border: 'none', cursor: 'pointer',
            fontFamily: T.mono, fontSize: 9, letterSpacing: '.12em', textTransform: 'uppercase',
            color: T.muted, padding: 4,
          }}>
            Fermer
          </button>
        </div>
      </div>
    </div>
  )
}
